import { useState, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const ANREDE_OPTIONS = [
  { value: '', label: '– Bitte wählen –' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
  { value: 'Divers', label: 'Divers' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof. Dr.', label: 'Prof. Dr.' },
];

function Kontakt() {
  const clientRef = useRef<ReturnType<typeof generateClient<Schema>> | null>(null);
  const getClient = () => {
    if (!clientRef.current) {
      clientRef.current = generateClient<Schema>();
    }
    return clientRef.current;
  };

  const [loading, setLoading] = useState(false);
  const [formFading, setFormFading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    unternehmenInstitution: '',
    anrede: '',
    vorname: '',
    nachname: '',
    position: '',
    email: '',
    telefon: '',
    message: '',
    interesseAustausch: false,
    projektUpdates: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await getClient().models.ContactSubmission.create(
        {
          unternehmenInstitution: formData.unternehmenInstitution.trim(),
          anrede: formData.anrede || undefined,
          vorname: formData.vorname.trim(),
          nachname: formData.nachname.trim(),
          position: formData.position.trim() || undefined,
          email: formData.email.trim(),
          telefon: formData.telefon.trim() || undefined,
          message: formData.message.trim(),
          interesseAustausch: formData.interesseAustausch,
          projektUpdates: formData.projektUpdates,
        },
        { authMode: 'identityPool' }
      );
      // Fade the form out, then swap it for the success message
      setFormFading(true);
      setTimeout(() => setSubmitted(true), 420);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      setError(message);
      setLoading(false);
    }
  };

  return (
    <div className="kontakt-container">
      <div className="content-card kontakt-form-card">

        {submitted ? (
          <div className="kontakt-submitted" role="alert">
            <div className="logo">Kontakt</div>
            <div className="kontakt-success">
              Vielen Dank für Ihre Nachricht. Wir melden uns in Kürze bei Ihnen.
            </div>
            <p className="kontakt-fallback">
              Oder schreiben Sie uns direkt: <a href="mailto:info@st-5.eu">info@st-5.eu</a>
            </p>
          </div>
        ) : (
          <div
            className="kontakt-form-wrapper"
            style={{ opacity: formFading ? 0 : 1 }}
          >
            <div className="logo">Kontakt</div>
            <p className="kontakt-intro">
              Haben Sie Fragen zum Projekt? Schreiben Sie uns gerne.
            </p>

            {error && (
              <div className="kontakt-error" role="alert">
                {error}
              </div>
            )}

            <form className="kontakt-form" onSubmit={handleSubmit}>
              <div className="form-row form-row-split">
                <div className="form-row-third">
                  <label htmlFor="unternehmenInstitution">
                    Unternehmen/Institution <span className="required">*</span>
                  </label>
                  <input
                    id="unternehmenInstitution"
                    name="unternehmenInstitution"
                    type="text"
                    required
                    value={formData.unternehmenInstitution}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="form-row-third">
                  <label htmlFor="position">Position</label>
                  <input
                    id="position"
                    name="position"
                    type="text"
                    value={formData.position}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="form-row-anrede">
                  <label htmlFor="anrede">Anrede</label>
                  <select
                    id="anrede"
                    name="anrede"
                    value={formData.anrede}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {ANREDE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row form-row-split">
                <div className="form-row-half">
                  <label htmlFor="vorname">
                    Vorname <span className="required">*</span>
                  </label>
                  <input
                    id="vorname"
                    name="vorname"
                    type="text"
                    required
                    value={formData.vorname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="form-row-half">
                  <label htmlFor="nachname">
                    Nachname <span className="required">*</span>
                  </label>
                  <input
                    id="nachname"
                    name="nachname"
                    type="text"
                    required
                    value={formData.nachname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row form-row-split">
                <div className="form-row-half">
                  <label htmlFor="email">
                    E-Mail <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="form-row-half">
                  <label htmlFor="telefon">Telefon</label>
                  <input
                    id="telefon"
                    name="telefon"
                    type="tel"
                    value={formData.telefon}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="message">
                  Nachricht <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="form-row form-row-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="interesseAustausch"
                    checked={formData.interesseAustausch}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  Ich habe Interesse an einem persönlichen Austausch, bitte kontaktieren Sie mich.
                </label>
              </div>

              <div className="form-row form-row-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="projektUpdates"
                    checked={formData.projektUpdates}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  Ich möchte über das Projekt und Beteiligungsmöglichkeiten auf dem Laufenden gehalten werden.
                </label>
              </div>

              <div className="form-row form-row-submit">
                <button type="submit" className="kontakt-submit" disabled={loading}>
                  {loading ? 'Wird gesendet…' : 'Absenden'}
                </button>
              </div>
            </form>

            <p className="kontakt-fallback">
              Oder schreiben Sie uns direkt: <a href="mailto:info@st-5.eu">info@st-5.eu</a>
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

export default Kontakt;
